import Rpc from "@/Rpc";
import ActivityManager from "@/activity/ActivityManager";
import JSZip from "jszip";
import FileSaver from 'file-saver';

import CompanyConfigurationFormsLoader from "@/components/forms/CompanyConfigurationFormsLoader";
import FormsLoader from "@/components/forms/FormsLoader";
import FormsActions from "@/components/forms/FormsActions";
import SessionManager from "@/utils/SessionManager";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import CommodityGroup from "@/server/com/silkplm/model/plm/dictionaries/CommodityGroup";
import Client from "@/server/com/silkplm/model/plm/dictionaries/Client";
import DialogManager from "@/utils/DialogManager";
import _ from "lodash";
import formConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormConfig";
import {getFilteredRecordsFromDatabaseWithRows} from "@/components/forms/customizations/plm/DynamicColumnsService";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import Category from "@/server/com/silkplm/model/plm/Category";
import FormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormFieldConfig";
import Order from "@/server/com/silkplm/model/plm/Order";
import Delegation from "@/server/com/silkplm/model/plm/Delegation";
import TechDetailsComponentIndexDto from "@/server/com/silkplm/model/plm/dictionaries/TechDetailsComponentIndexDto";
import ComponentIndex from "@/server/com/silkplm/model/plm/dictionaries/ComponentIndex";

const CloneActivity = () => require("@/components/forms/CloneActivity");
const VersionChartActivity = () => require("@/components/forms/VersionChartActivity");

const BrandingProductInfo = () => require("@/components/plm/BrandingProductInfo");

export function checkIfEnabledByFeatureFlags(assignedFeatureFlags) {
    if (!assignedFeatureFlags || !assignedFeatureFlags.length) {
        return true;
    }
    for (let assignedFeatureFlag of assignedFeatureFlags) {
        for (let featureFlagFromSession of SessionManager.featureFlags) {
            if (assignedFeatureFlag === featureFlagFromSession) {
                return true;
            }
        }
    }
    return false;
}

export async function handleCreateOrderForMaterialsButton(selectionLocal, context, form, streamingQuery, streamSelection) {
    if (!selectionLocal) return;

    let hasMerchantGroup = await Rpc.Users.checkIfSessionUserHasMerchantRole();
    if (!hasMerchantGroup) {
        await DialogManager.info(
            $tr("createOrderForMaterialsButton.error.empty.materials.title",
                "An error occurred while attempting to add raw materials to the order."
            ),
            $tr("createOrderForMaterialsButton.error.no.merchant.group",
                "You cannot place an Order because you are not assigned to a Merchant group."
            )
        );
        return;
    }

    let records = await Rpc.plm.services.BillOfMaterialsService.materializeTechDetailsEntities(streamingQuery, streamSelection);

    let supplierIds = new Set();
    records.forEach(record => {
        supplierIds.add(record.supplierId);
    })
    let checkIfAllFieldsAreFilledIn = await Rpc.plm.services.TechnologicalService.checkIfAllFieldsAreFilledIn(records.map(obj => obj.id)).lock();

    if (checkIfAllFieldsAreFilledIn.length > 0) {
        await DialogManager.info(
            $tr("createOrderForMaterialsButton.error.empty.materials.title", "An error occurred while attempting to add raw materials to the order."),
            $tr("createOrderForMaterialsButton.error.empty.materials.body", "You have selected raw materials that lack a Component Index. Please check the following records: ")
            + checkIfAllFieldsAreFilledIn
        );
        return;
    }

    let orderedTechDetails = await Rpc.plm.services.TechnologicalService.checkIfAllTechDetailsHasNoNumberOfOrder(records.map(obj => obj.id)).lock();
    if (orderedTechDetails.length > 0) {
        await DialogManager.info(
            $tr("createOrderForMaterialsButton.error.empty.materials.title", "An error occurred while attempting to add raw materials to the order."),
            $tr("createOrderForMaterialsButton.error.already.ordered", "You have selected raw materials that has already Ordered Number. Please check the following records: ")
            + orderedTechDetails
        );
        return;
    }

    let supplierId = null;
    if (supplierIds.size === 1) {
        let id = supplierIds.values().next().value;
        if (id) {
            supplierId = id;
        }
    }
    let localContext = {
        ...context,
        presetFields: [
            {fieldName: "orderForRawMaterials", value: true},
            {fieldName: "merchant", value: SessionManager.applicationUser.id},
            {fieldName: "insertByTechDetails", value: true},
            {fieldName: "numberOfOrder", value: -1000000},
            {fieldName: "techDetailsIds", value: records.map(obj => obj.id)},
            {fieldName: "supplierId", value: supplierId},
        ]
    };

    let formConfigOrder = FormsLoader.detectFormConfig(Order.getRemoteClass());
    await FormsActions.create(formConfigOrder, localContext);
}


export async function createBillOfMaterialsSummary(selectionLocal, streamingQuery, streamSelection) {
    if (!selectionLocal) return;
    let recordsIds = await Rpc.plm.services.BillOfMaterialsService.materializeTechDetails(streamingQuery, streamSelection);
    let checkIfAllFieldsAreFilledIn = await Rpc.plm.services.TechnologicalService.checkIfAllFieldsAreFilledIn(recordsIds).lock();

    if (checkIfAllFieldsAreFilledIn.length > 0) {
        await DialogManager.info(
            $tr("createOrderForMaterialsButton.error.empty.materials.title", "An error occurred while attempting to add raw materials to the order."),
            $tr("createOrderForMaterialsButton.error.empty.materials.body", "You have selected raw materials that lack a Component Index. Please check the following records: ")
            + checkIfAllFieldsAreFilledIn
        );
        return;
    }
    let results = await Rpc.plm.services.BillOfMaterialsService.generateTechDetailsComponentIndexDto(recordsIds);
    let activity = await FormsActions.showList(TechDetailsComponentIndexDto, {}, []);

    let entitiesList = null;
    entitiesList = activity?.$refs?.formList?.$refs?.entititesList;
    entitiesList.setCustomData(results);

    let selectedRecords = await activity.getResult();
    selectedRecords.forEach((async entity => {
        entity.userModifyInstance = true;
        await entity.save();
    }));
}

export function filterToolboxButtonsAfterMounted(toolboxButtons, entityInstance) {
    if (entityInstance instanceof Order) {
        if (entityInstance.orderForRawMaterials) {
            toolboxButtons = toolboxButtons.filter(button => (button.label !== $tr("exportBrandingFolder", "Export Branding Folder") &&
                button.label !== "Contract Printout"));
        }
        if (!entityInstance.orderForRawMaterials) {
            toolboxButtons = toolboxButtons.filter(button => button.label !== $tr("entityEditForm.orderDetailsPrintout"));
        }
    }
    return toolboxButtons;
}

export function checkIfEnabledByOrganizationFormsSettingsForFormField(form, fieldConfig) {

    let fieldConfigId;
    let fieldName;

    if (fieldConfig instanceof FormFieldConfig) {
        fieldConfigId = fieldConfig.fieldConfigId
        fieldName = fieldConfig.fieldName;
    } else {
        fieldConfigId = fieldConfig;
        fieldName = fieldConfig;
    }

    if (fieldName === "EID") {
        return true;
    }
    let formId = form.formId;
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.fieldsConfiguration) {
        return true;
    }
    let fieldsFromAllFormsConfiguration = companyConfiguration.fieldsConfiguration;
    let singleFormAllFieldsConfiguration = fieldsFromAllFormsConfiguration[formId];
    if (!singleFormAllFieldsConfiguration) {
        return true;
    }
    let singleFormFieldConfiguration = null;
    if (form.isView) {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfigId];
    } else {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldName];
    }
    if (!singleFormFieldConfiguration) {
        return true;
    }
    return singleFormFieldConfiguration.active;
}

export function findFieldValueFromCompanyCompanyConfigurationFormField(form, fieldConfig, companyConfigurationFormFieldName) {
    if (!form) {
        return null;
    }

    if (!fieldConfig?.fieldName) {
        return null;
    }
    let fieldName = fieldConfig.fieldName;
    if (fieldName.includes("EID")) {
        return null;
    }
    let formId = form.formId;
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.fieldsConfiguration) {
        return null;
    }
    let fieldsFromAllFormsConfiguration = companyConfiguration.fieldsConfiguration;
    let singleFormAllFieldsConfiguration = fieldsFromAllFormsConfiguration[formId];
    if (!singleFormAllFieldsConfiguration) {
        return null;
    }
    let singleFormFieldConfiguration = null;
    if (form.isView) {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfig.fieldConfigId];
    } else {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldName];
    }
    if (!singleFormFieldConfiguration) {
        return null;
    }
    return singleFormFieldConfiguration[companyConfigurationFormFieldName];
}

export function findFormOrderForFormField(form, fieldConfig) {
    let formId = form.formId;
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    let fieldsFromAllFormsConfiguration = companyConfiguration.fieldsConfiguration;
    let singleFormAllFieldsConfiguration = fieldsFromAllFormsConfiguration[formId];
    if (!singleFormAllFieldsConfiguration) {
        return fieldConfig.formOrder;
    }
    let singleFormFieldConfiguration = null;
    if (form.isView) {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfig.fieldConfigId];
    } else {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfig.fieldName];
    }
    if (!singleFormFieldConfiguration) {
        return fieldConfig.formOrder;
    }
    return singleFormFieldConfiguration.formOrder;
}

export function findGridInlineConfiguration(masterFormConfig, fieldName) {
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    let fieldsFromAllFormsConfiguration = companyConfiguration.fieldsConfiguration;
    let singleFormAllFieldsConfiguration = fieldsFromAllFormsConfiguration[masterFormConfig.formId];
    if (!singleFormAllFieldsConfiguration) {
        return null;
    }
    return singleFormAllFieldsConfiguration[fieldName];
}

export function findGridColOrderForFormField(form, fieldConfig) {
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.fieldsConfiguration) {
        return fieldConfig.gridColOrder;
    }
    let fieldsFromAllFormsConfiguration = companyConfiguration.fieldsConfiguration;
    let singleFormAllFieldsConfiguration = fieldsFromAllFormsConfiguration[form.formId];
    if (!singleFormAllFieldsConfiguration) {
        return fieldConfig.gridColOrder;
    }
    let singleFormFieldConfiguration = null;
    if (form.isView) {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfig.fieldConfigId];
    } else {
        singleFormFieldConfiguration = singleFormAllFieldsConfiguration[fieldConfig.fieldName];
    }
    if (!singleFormFieldConfiguration) {
        return fieldConfig.gridColOrder;
    }
    return singleFormFieldConfiguration.formOrder;
}

export function checkIfEnabledByOrganizationFormsSettingsForForm(formId) {
    if (!FormsLoader.formInEnabledModules(formId)) {
        return false;
    }

    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.formsConfiguration) {
        return true;
    }
    let formsConfiguration = companyConfiguration.formsConfiguration;
    let singleFormConfiguration = formsConfiguration[formId];
    if (!singleFormConfiguration) {
        return true;
    }
    return singleFormConfiguration.active;
}

export function checkIfSectionIsEnabled(menuGroup) {
    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    let formsConfiguration = companyConfiguration.formsConfiguration;
    let enabledConfigurationForSection;
    for (const [formId, config] of Object.entries(formsConfiguration)) {
        if (config.menuGroupInfo === menuGroup) {
            if (config.active) {
                return true;
            } else {
                enabledConfigurationForSection = true;
            }
        }
    }
    if (enabledConfigurationForSection) {
        return false;
    }
    return true;
}

export function findFieldValueFromCompanyCompanyConfigurationForm(formId, companyConfigurationFormFieldName) {

    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.formsConfiguration) {
        return true;
    }
    let formsConfiguration = companyConfiguration.formsConfiguration;
    let singleFormConfiguration = formsConfiguration[formId];
    if (!singleFormConfiguration) {
        return null;
    }
    return singleFormConfiguration[companyConfigurationFormFieldName];
}

export function findCompanyConfigurationForm(formId) {

    let companyConfiguration = CompanyConfigurationFormsLoader.getConfig();
    if (!companyConfiguration || !companyConfiguration.formsConfiguration) {
        return true;
    }
    let formsConfiguration = companyConfiguration.formsConfiguration;
    let singleFormConfiguration = formsConfiguration[formId];
    if (!singleFormConfiguration) {
        return null;
    }
    return singleFormConfiguration;
}

export async function exportBrandingFolderForOrderFromList(toolbox, selectionLocal, streamingEngine) {
    if (selectionLocal instanceof Array) {
        if (selectionLocal.length === 1) {
            let entity = selectionLocal[0];
            if (entity.orderForRawMaterials) {
                let brandingToolboxIndex = _.findIndex(toolbox, tool =>
                    tool.label === $tr("exportBrandingFolder", "Export Branding Folder"));
                toolbox[brandingToolboxIndex].disabled = true;
                return;
            } else {
                let brandingToolboxIndex = _.findIndex(toolbox, tool =>
                    tool.label === $tr("exportBrandingFolder", "Export Branding Folder"));
                toolbox[brandingToolboxIndex].disabled = false;
            }
            if (!entity.id) {
                entity = streamingEngine.dataGrid.$data.filteredRows[0];
            }
            await exportBrandingFolderForOrder(entity, entity.id);
        }
    }
}

export async function exportBrandingFolderForOrder(entityInstance, entityId, downloadingBrandingZip) {
    await Rpc.plm.services.BrandingService.generateBrandingFolderForOrder(entityId);
}

export async function collectDataFromProductToBranding(entityId) {
    let productInfoBrandingString = await Rpc.plm.services.ProductService.collectDataFromProductToBranding(entityId).lock();

    await ActivityManager.showActivity(BrandingProductInfo, {
        brandingProductInfo: productInfoBrandingString
    });
}

export function findEditEnabledFormBySecuredFields(formConfig, permissions) {
    let editEnabledBySecuredFields = false;
    if (formConfig.securedFields?.length) {
        for (let securedFieldConfig of formConfig.securedFields) {
            if (permissions[securedFieldConfig.effectivePermissionWrite]) {
                editEnabledBySecuredFields = true;
            }
        }
    }
    return editEnabledBySecuredFields;
}

export function removeLeftMenuItemsWhenHasNoItems(forms, LEFT_MENU) {
    let ACTIVE_ITEMS_LEFT_MENU = [];
    LEFT_MENU.forEach(section => {
        let itemsWithoutInactive = [];
        if (section.items?.length) {
            for (let subSection of section.items) {
                if (subSection.id === 'dict-defined') {
                    itemsWithoutInactive.push(subSection);
                    continue;
                }
                if (subSection.action !== undefined || subSection.items && subSection.items.length > 0) {
                    itemsWithoutInactive.push(subSection);
                    continue;
                }
                for (let form of forms) {
                    if (subSection.id && subSection.id === form.menuGroup) {
                        itemsWithoutInactive.push(subSection);
                        break;
                    }
                }
            }
        }
        section.items = itemsWithoutInactive;
        if (checkIfSectionIsEnabled(section.id)) {
            ACTIVE_ITEMS_LEFT_MENU.push(section);
        }
    });
    return ACTIVE_ITEMS_LEFT_MENU;
}


export async function cloneEntity(entityInstance, entityId) {
    let form = FormsLoader.detectFormConfig(entityInstance);
    //let cloningOptions = await DialogManager.cloneConfirmation(form, entityInstance, entityId);
    let activity = await ActivityManager.showActivity(CloneActivity, {
        form, entityId, entityInstance
    });
    let cloningOptions = await activity.getResult();

    if (cloningOptions) {
        DialogManager.openPendingDialog($tr('entityEdit.crate.copy', 'Creating copy'),
            $tr('entityEdit.duplicated.record', 'Record is now duplicated...'));
        try {
            let newRecord = await Rpc.Versioning.clone(form.formClassLong, entityId, cloningOptions);
            if (newRecord) {
                FormsActions.edit(newRecord).then();
            }
        } catch (e) {
            if (e instanceof RpcInputValidationException) {
                DialogManager.info($tr('entityEdit.cloning.fail', 'Cloning failed'), e.errorMessage);
            } else {
                console.log(e);
                DialogManager.errorNotification(e);
            }
        } finally {
            DialogManager.closePendingDialog();
        }
    }
}

export async function showGraph(entityInstance, entityId) {
    let form = FormsLoader.detectFormConfig(entityInstance);
    await ActivityManager.showActivity(VersionChartActivity, {
        form, entityId, entityInstance
    });
}

export async function generateOrderNumber(formConfig, form, entity) {
    if (form.unsavedChanges) {
        if (!await DialogManager.yesNo($tr('entityEdit.unsavedChanges', 'Unsaved changes'),
            $tr('entityEdit.unsaved.changed', 'There are unsaved changed in the form. Do you want assign Order Number and lose unsaved data?'))) {
            return;
        }
    }
    if (entity.orderNumber) {
        if (!await DialogManager.yesNo($tr('entityEdit.orderNumber.assigned.no0', 'Number already assigned'),
            $tr('entityEdit.orderNumber.assigned.no1', 'This product ') +
            entity.name +
            $tr('entityEdit.orderNumber.assigned.no2', 'already has an assigned order number: ') +
            entity.orderNumber +
            $tr('entityEdit.orderNumber.assigned.no3', '. Generate a new order number for this product?'))) {
            return;
        }
    }

    if (!entity.seasonId) {
        await DialogManager.info($tr('entityEdit.cannot.generate.season', 'Cannot generate an Order number.'), $tr("entityEdit.noSeason", "No <br>Season</br> assign."));
    }

    let checkLevel3CategorySymbols = await Rpc.plm.services.ProductInSeasonService.checkLevelCategorySymbols(entity.id, 2);
    if (!checkLevel3CategorySymbols) {
        await DialogManager.info($tr('entityEdit.cannot.generate.category.level.3', 'Cannot generate an Order number.'), $tr("entityEdit.noLevel3", "Missing level 3 <b>Category</b> symbol."));
        return;
    }

    let checkLevel4CategorySymbols = await Rpc.plm.services.ProductInSeasonService.checkLevelCategorySymbols(entity.id, 3);
    if (!checkLevel4CategorySymbols) {
        await DialogManager.info($tr('entityEdit.cannot.generate.category.level.4', 'Cannot generate an Order number.'), $tr("entityEdit.noLevel4", "Missing level 4 <b>Category</b> symbol."));
        return;
    }

    if (entity.__parent) {
        let parentRecord = await Rpc.Forms.getEntity(formConfig.formClassLong, entity.__parent);
        if (!parentRecord.orderNumber?.length) {
            if (await DialogManager.yesNo($tr('entityEdit.record.orderNo', 'Parent record has no assigned Order Number'),
                $tr('entityEdit.want.orderNo', 'Do you want to assign an Order Number for the parent record?'))) {
                let result = await generateOrderNumber(formConfig, form, parentRecord);
                if (!result) {
                    return;
                }
            } else {
                return;
            }
        }
    }
    return Rpc.plm.services.ProductInSeasonService.generateOrderNumberForProductInSeasonFromButton(entity.id);
}

export async function setEditToolboxButtonAfterEditForm(entity, toolboxButtons, hasWriteAccess) {
    let generateOrderNumberButton = _.find(toolboxButtons, {label: $tr('entityEdit.generate', 'Generate Order Number')});
    if (generateOrderNumberButton) {
        if (entity.orderNumber?.length) {
            generateOrderNumberButton.disabled = true;
            return;
        }
        generateOrderNumberButton.disabled = !hasWriteAccess;
    }
}

export async function cloneDataFromAnotherDimensionsTab(masterFormClassLong, masterId) {

    let actualProductInSeason = await Rpc.Forms.getEntity(masterFormClassLong, masterId);
    let uniqueSizesArray = [];
    let excludedIds = [];
    uniqueSizesArray.push(actualProductInSeason.sizeRange)
    excludedIds.push(masterId)
    let filter = [];
    let filterWithSizeRange =
        {
            fieldLabel: "Size Range",
            fieldName: "sizeRange",
            filterSpec: {
                idValues: uniqueSizesArray
            },
            records: {
                idValues: uniqueSizesArray
            }
        };

    filter = {customFilter: [filterWithSizeRange]};

    let result = await (await FormsActions.showSelectList(ProductInSeason, {
        filter,
        showAdd: false,
        showDelete: false,
    }, excludedIds)).getResult();

    if (result) {
        await Rpc.plm.services.DimensionsService.copyDynamicData(result, actualProductInSeason);
    }
}

export async function orderDetailsPrintoutRawMaterialsPath(entityInstance, language, remember) {

    let shouldRemember = true;

    this.result = await DialogManager.printoutLanguageDialog(shouldRemember, language, undefined,
        undefined, undefined, false, undefined, false);
    await Rpc.plm.services.OrderService.orderDetailsPrintoutRawMaterialsPath(entityInstance.id, this.result.language);
    return this.result;
}